import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Box, Divider, useMediaQuery, useTheme, IconButton, Tooltip } from '@mui/material';
import { Dashboard, People, Event, PersonAdd, Category, Queue as QueueIcon, Email, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { Role } from '../../types/user.types';
import { useState, useEffect } from 'react';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  allowedRoles: Role[];
}

// Menu items principali (accessibili a tutti i ruoli)
const mainMenuItems: MenuItem[] = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
    allowedRoles: [Role.ADMIN, Role.OPERATORE, Role.VISUALIZZATORE]
  },
  {
    text: 'Scadenze',
    icon: <Event />,
    path: '/scadenze',
    allowedRoles: [Role.ADMIN, Role.OPERATORE, Role.VISUALIZZATORE]
  },
  {
    text: 'Clienti',
    icon: <People />,
    path: '/clienti',
    allowedRoles: [Role.ADMIN, Role.OPERATORE, Role.VISUALIZZATORE]
  }
];

// Menu items amministrativi (solo Admin)
const adminMenuItems: MenuItem[] = [
  {
    text: 'Utenti',
    icon: <PersonAdd />,
    path: '/users',
    allowedRoles: [Role.ADMIN]
  },
  {
    text: 'Tipologie Scadenze',
    icon: <Category />,
    path: '/tipologie',
    allowedRoles: [Role.ADMIN]
  },
  {
    text: 'Template Email',
    icon: <Email />,
    path: '/email-templates',
    allowedRoles: [Role.ADMIN]
  },
  {
    text: 'Queue Dashboard',
    icon: <QueueIcon />,
    path: '/queue',
    allowedRoles: [Role.ADMIN]
  }
];

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // <900px
  
  // State for collapsed/expanded sidebar (only for desktop)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState === 'true';
  });
  
  // Save collapsed state to localStorage
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('sidebarCollapsed', String(isCollapsed));
    }
  }, [isCollapsed, isMobile]);
  
  const toggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close drawer on mobile after navigation
    if (isMobile) {
      onClose();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, path: string) => {
    // Keyboard accessibility: Enter and Space should trigger navigation
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigation(path);
    }
  };

  const filteredMainMenuItems = mainMenuItems.filter((item) =>
    user?.ruolo && item.allowedRoles.includes(user.ruolo)
  );

  const filteredAdminMenuItems = adminMenuItems.filter((item) =>
    user?.ruolo && item.allowedRoles.includes(user.ruolo)
  );

  const renderMenuItems = (items: MenuItem[], collapsed: boolean = false) =>
    items.map((item) => {
      const button = (
        <ListItemButton
          selected={location.pathname === item.path}
          onClick={() => handleNavigation(item.path)}
          onKeyDown={(e) => handleKeyDown(e, item.path)}
          aria-current={location.pathname === item.path ? 'page' : undefined}
          aria-label={`Vai a ${item.text}`}
          role="link"
          tabIndex={0}
          sx={{
            minHeight: 48,
            justifyContent: collapsed ? 'center' : 'initial',
            px: 2.5,
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '-2px',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: collapsed ? 'auto' : 3,
              justifyContent: 'center',
              color: location.pathname === item.path ? 'inherit' : 'text.secondary',
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText 
            primary={item.text} 
            sx={{
              opacity: collapsed ? 0 : 1,
              display: collapsed ? 'none' : 'block'
            }}
          />
        </ListItemButton>
      );
      
      return (
        <ListItem key={item.text} disablePadding>
          {collapsed ? (
            <Tooltip title={item.text} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            button
          )}
        </ListItem>
      );
    });

  const drawerContent = (collapsed: boolean = false) => (
    <>
      <Toolbar />
      <Box
        component="nav"
        aria-label="Menu di navigazione principale"
        sx={{ 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        {/* Toggle button for desktop only */}
        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', px: 1, pb: 2 }}>
            <IconButton
              onClick={toggleCollapse}
              size="small"
              aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}
              sx={{
                backgroundColor: 'action.hover',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>
        )}
        
        {/* Menu principale */}
        <List role="navigation">
          {renderMenuItems(filteredMainMenuItems, collapsed)}
        </List>
        
        {/* Divider prima delle sezioni amministrative */}
        {filteredAdminMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <List role="navigation">
              {renderMenuItems(filteredAdminMenuItems, collapsed)}
            </List>
          </>
        )}
      </Box>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{ 
        width: { md: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH }, 
        flexShrink: { md: 0 },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
      aria-label="menu laterale"
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent(false)}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent(isCollapsed)}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
